import { Link } from "react-router-dom";
import { SecondaryLink, VotingMechanicsDemo } from "../components/ui";
import {
	IconClock,
	IconDecide,
	IconGather,
	IconLink,
	IconMath,
	IconShield,
	IconVote,
} from "../components/icons/BrandIcons";
import { CUSTOMER_CONTACT_EMAIL, CUSTOMER_CONTACT_MAILTO } from "../data/contact";

const FAQ_ITEMS: { q: string; a: string }[] = [
	{
		q: "How do I run a poll?",
		a: "From the home page, write your question, add two or more options, open Poll settings if you want to change voting style or time limit, acknowledge the Curator warning, then choose Launch the poll. You’ll get a voter link and a private Curator link — save both.",
	},
	{
		q: "What is the Curator link?",
		a: "It’s the private URL that lets you administer the poll (open voting, finalize). Anyone with that link has curator access. Treat it like a physical key. If you lose it and your browser cookie is gone, we can’t recover the room for you.",
	},
		{
			q: "Ranked choice vs weighted picks — which should I pick?",
			a: "Ranked choice (Borda count) rewards options with broad support across the stack. Weighted picks (quadratic voting) are for when you want people to show intensity on a shared points budget without one person shouting everyone else down. Both are honest about tradeoffs; neither pretends to read minds.",
		},
	{
		q: "Can someone change their vote?",
		a: "While voting is open, a voter can use Change my vote on the invitation page if they need to adjust. After the Curator finalizes, the tally is fixed for that room.",
	},
	{
		q: "Why did my room disappear or say expired?",
		a: "Rooms honor the time limit you chose at launch. After that window, the room is read-only if results were finalized, or simply done if not. Plan your window before you share links.",
	},
	{
		q: "Live updates look stuck — what should I try?",
		a: "Refresh once. If you’re on the Curator view, make sure your Curator Link still matches the room; WebSockets need a healthy connection, and ad blockers rarely interfere but corporate proxies sometimes do.",
	},
	{
		q: "Do you sell my email or build a dossier on participants?",
		a: "There’s no account wall for voters. We minimize what we collect and what we retain; see our Privacy page for today’s web behavior versus longer-term integration plans.",
	},
	{
		q: "Is this “cryptographically anonymous”?",
		a: "No spy-novel promises. Voters get a lightweight device id in the browser so we can enforce one ballot per device during a room — that’s practical for a workshop, not a nation-state threat model.",
	},
];

const PROMISE_ICONS = [
	{ Icon: IconGather, label: "Gather" },
	{ Icon: IconVote, label: "Vote" },
	{ Icon: IconDecide, label: "Decide" },
	{ Icon: IconShield, label: "Private" },
	{ Icon: IconLink, label: "Curator key" },
	{ Icon: IconClock, label: "Expires" },
	{ Icon: IconMath, label: "Honest math" },
] as const;

export function AboutPage() {
	return (
		<div className="wi-editorial-prose space-y-xl px-1 pb-24 sm:px-0">
			<header className="space-y-md">
				<p className="font-geist text-caption text-wi-text/50 uppercase tracking-[0.16em]">About WorthyIdea</p>
				<h1 className="wi-type-truth text-h1 text-wi-text sm:text-display">
					From noisy discussion to one outcome you can point to.
				</h1>
				<div className="space-y-sm text-body text-wi-text/82">
					<p>
						Groups drown in opinions. WorthyIdea is a{" "}
						<strong className="font-medium text-wi-text">friction-light poll</strong>: the Curator frames the question,
						voters use clear rules, and you leave with a defensible result — without enterprise workflow weight.
					</p>
					<p>
						Right now, WorthyIdea works great as a standalone tool — no account, no integrations required. Slack and Jira
						are next, so your team can vote where you already work, without your data ever leaving your own Slack or Jira
						instance when those integrations ship.
					</p>
				</div>
				<ul className="flex flex-wrap gap-md pt-xs" aria-label="Product promises">
					{PROMISE_ICONS.map(({ Icon, label }) => (
						<li key={label} className="flex flex-col items-center gap-1.5 text-center">
							<Icon className="h-7 w-7" />
							<span className="font-geist text-[10px] uppercase tracking-[0.12em] text-wi-text/50">{label}</span>
						</li>
					))}
				</ul>
			</header>

			<section id="problem" className="space-y-md scroll-mt-20">
				<h2 className="wi-type-truth text-h2 text-wi-text">The problem</h2>
				<div className="space-y-sm text-body text-wi-text/80">
					<p>
						A simple question becomes a long thread, a calendar pile-up, or a loudest-voice contest. By the time something
						is &quot;decided,&quot; the room rarely agrees on what was actually decided — or why.
					</p>
					<p>
						WorthyIdea compresses that spiral: one question, ranked or quadratic voting with transparent rules, live
						presence so the Curator knows when to move, and a finalize step that refuses to fake consensus when the room
						is empty or split.
					</p>
					</div>
				</section>

				<section id="ethics" className="space-y-md scroll-mt-20">
					<h2 className="wi-type-truth text-h2 text-wi-text">Our privacy promise</h2>
				<div className="space-y-sm text-body text-wi-text/80">
					<p>
						We bias toward <strong className="font-medium text-wi-text">anonymous voting</strong> and{" "}
						<strong className="font-medium text-wi-text">no account wall for voters</strong>. The Curator path is
						deliberately capable: your <strong className="font-medium text-wi-text">Curator Link is a key</strong> — save
						it, or you risk losing admin access when cookies clear.
					</p>
					<p>
						We do <strong className="font-medium text-wi-text">not</strong> claim that nothing is stored anywhere.
						Today&apos;s web polls keep ballots and room metadata for a TTL window so you can recover with your Curator
						Link — they <strong className="font-medium text-wi-text">expire on your schedule</strong>, and nothing
						outlives its purpose. Clearing temporary session memory after a host Consensus Record is a separate
						enterprise path, not a claim about this site.
					</p>
					<p>
						<Link to="/privacy" className="font-medium text-wi-gold underline-offset-2 hover:underline">
							Read the Privacy page
						</Link>{" "}
						for today vs roadmap;{" "}
							<Link to="/trust/security" className="font-medium text-wi-gold underline-offset-2 hover:underline">
								Trust Center · Security
							</Link>{" "}
							for the architecture narrative, or{" "}
							<Link to="/security" className="font-medium text-wi-gold underline-offset-2 hover:underline">
								Security controls
							</Link>{" "}
							for the engineering matrix.
						</p>
					</div>
				</section>

				<section id="try-voting" className="space-y-md scroll-mt-20">
					<h2 className="wi-type-truth text-h2 text-wi-text">Feel the voting styles</h2>
					<p className="text-body-sm text-wi-text/75">
						A short interactive demo — the same Ranked choice and Weighted picks controls voters use.
					</p>
					<VotingMechanicsDemo />
				</section>

				<section id="math" className="space-y-sm border-wi-text/10 border-t pt-xl scroll-mt-20">
				<h2 className="wi-type-truth text-h2 text-wi-text">The math, in human terms</h2>
				<p className="wi-type-faq text-wi-text/72">
					<strong className="text-wi-text">Borda</strong> (ranked) awards more points to higher ranks so broad support
					across the stack can win. <strong className="text-wi-text">Quadratic</strong> spending uses a shared budget with
					marginal cost growing as <span className="font-medium text-wi-text">v²</span> so intensity shows up without one
					voter buying the chart. Ties surface as joint winners; the chart can mark near-ties after normalization.
				</p>
				<p className="wi-type-faq text-wi-text/65">
					For derivations, tie behavior, and a short glossary, see{" "}
					<Link to="/math" className="font-medium text-wi-gold underline-offset-2 hover:underline">
						The math →
					</Link>
				</p>
			</section>

			<section className="space-y-sm border-wi-text/10 border-t pt-xl">
				<h2 className="wi-type-truth text-h2 text-wi-text">Made by humans</h2>
				<div className="space-y-sm text-body text-wi-text/80">
					<p>
						WorthyIdea is built by people who care about legible decisions and calm interfaces —{" "}
						<strong className="font-medium text-wi-text">thoughtful motion</strong>, honest copy, and edges that
						don&apos;t pretend to be more than they are. We run on Cloudflare&apos;s edge so sessions stay snappy.
					</p>
				</div>
			</section>

			<section className="space-y-sm border-wi-text/10 border-t pt-xl">
				<h2 className="wi-type-truth text-h2 text-wi-text">Plans</h2>
				<p className="text-body-sm text-wi-text/75">
					Everyone gets fast, private polls for free. Upgrade when you&apos;re running them again and again. Teams can
					also email{" "}
					<a href={CUSTOMER_CONTACT_MAILTO} className="font-medium text-wi-gold underline-offset-2 hover:underline">
						{CUSTOMER_CONTACT_EMAIL}
					</a>
					.
				</p>
				<p className="pt-xs">
					<SecondaryLink to="/membership">See plans</SecondaryLink>
				</p>
			</section>

			<section id="faq" className="space-y-md border-wi-text/10 border-t pt-xl scroll-mt-20">
				<h2 className="wi-type-truth text-h2 text-wi-text">Questions &amp; quick fixes</h2>
				<p className="text-body-sm text-wi-text/65">
					Plain-language how-tos and the issues people actually run into. Open a row for the answer.
				</p>
				<div className="space-y-sm">
					{FAQ_ITEMS.map((item) => (
						<details key={item.q} className="wi-card group px-4 py-1">
							<summary className="cursor-pointer list-none py-3 font-medium text-body-sm text-wi-text marker:content-none [&::-webkit-details-marker]:hidden">
								<span className="inline-flex w-full items-center justify-between gap-3">
									<span>{item.q}</span>
									<span className="text-caption text-wi-text/35 transition group-open:rotate-180">▼</span>
								</span>
							</summary>
							<p className="wi-type-faq border-wi-text/8 border-t pb-3 pl-0.5 text-wi-text/72">{item.a}</p>
						</details>
					))}
				</div>
			</section>
		</div>
	);
}
